import { c as _c } from "react/compiler-runtime";
import { useTranslation } from '@/hooks/use-translation';
import { useMeasureOptions } from '@/hooks/store/use-measure-options-store';
import { KeyColor } from '@/lib/piano/keyboard';
import type { TouchWeightAnalyzedKeyboard } from '@/lib/piano/touch-design/touch-weight-key-analysis';
import { AnalyzeDataSheetTableRow } from './AnalyzeDataSheetTableRow';

/**
 * Every value of the instrument, key by key.
 *
 * The columns are grouped under two banners — what was measured and what the
 * tool worked out from it — because that distinction is the whole point of
 * the sheet: a reader checking a surprising figure needs to know at once
 * whether to doubt their gauge or their arithmetic.
 */
export const AnalyzeDataSheet: React.FC<{
  analyzedKeyboard: TouchWeightAnalyzedKeyboard;
}> = t0 => {
  const $ = _c(99);
  const {
    analyzedKeyboard
  } = t0;
  const {
    currentLanguage,
    t
  } = useTranslation();
  const {
    useManualSWRMeasurements,
    useSupportSpringMeasurements
  } = useMeasureOptions();
  let t1;
  if ($[0] !== t || $[1] !== useSupportSpringMeasurements) {
    t1 = useSupportSpringMeasurements ? [{
      abbreviation: "DS",
      name: t("D with spring")
    }] : [];
    $[0] = t;
    $[1] = useSupportSpringMeasurements;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t) {
    t2 = t("Down weight");
    $[3] = t;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t2) {
    t3 = {
      abbreviation: "D",
      name: t2
    };
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t) {
    t4 = t("Up weight");
    $[7] = t;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t4) {
    t5 = {
      abbreviation: "U",
      name: t4
    };
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t) {
    t6 = t("Front weight");
    $[11] = t;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t6) {
    t7 = {
      abbreviation: "FW",
      name: t6
    };
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t) {
    t8 = t("Strike weight");
    $[15] = t;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t8) {
    t9 = {
      abbreviation: "SW",
      name: t8
    };
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t || $[20] !== useManualSWRMeasurements) {
    t10 = useManualSWRMeasurements ? [{
      abbreviation: "SWR",
      name: t("Ratio")
    }] : [];
    $[19] = t;
    $[20] = useManualSWRMeasurements;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== t1 || $[23] !== t10 || $[24] !== t3 || $[25] !== t5 || $[26] !== t7 || $[27] !== t9) {
    t11 = [...t1, t3, t5, t7, t9, ...t10];
    $[22] = t1;
    $[23] = t10;
    $[24] = t3;
    $[25] = t5;
    $[26] = t7;
    $[27] = t9;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  const measuredColumns = t11;
  let t12;
  if ($[29] !== t || $[30] !== useSupportSpringMeasurements) {
    t12 = useSupportSpringMeasurements ? [{
      abbreviation: "BW",
      name: t("Balance (without spring)")
    }, {
      abbreviation: "BW \u2212 BWS",
      name: t("Balance (with spring)")
    }] : [{
      abbreviation: "BW",
      name: t("Balance")
    }];
    $[29] = t;
    $[30] = useSupportSpringMeasurements;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== t) {
    t13 = t("Friction");
    $[32] = t;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== t13) {
    t14 = {
      abbreviation: "F",
      name: t13
    };
    $[34] = t13;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== t) {
    t15 = t("Ratio");
    $[36] = t;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  let t16;
  if ($[38] !== t15) {
    t16 = {
      abbreviation: "R",
      name: t15
    };
    $[38] = t15;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  let t17;
  if ($[40] !== t || $[41] !== useSupportSpringMeasurements) {
    t17 = useSupportSpringMeasurements ? [{
      abbreviation: "BWS",
      name: t("Springs")
    }] : [];
    $[40] = t;
    $[41] = useSupportSpringMeasurements;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  let t18;
  if ($[43] !== t12 || $[44] !== t14 || $[45] !== t16 || $[46] !== t17) {
    t18 = [...t12, t14, t16, ...t17];
    $[43] = t12;
    $[44] = t14;
    $[45] = t16;
    $[46] = t17;
    $[47] = t18;
  } else {
    t18 = $[47];
  }
  const computedColumns = t18;
  let t19;
  if ($[48] !== t) {
    t19 = t("Every value of the instrument, key by key");
    $[48] = t;
    $[49] = t19;
  } else {
    t19 = $[49];
  }
  let t20;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <th className="border-b border-border bg-card" colSpan={2} />;
    $[50] = t20;
  } else {
    t20 = $[50];
  }
  const t21 = measuredColumns.length;
  let t22;
  if ($[51] !== t) {
    t22 = t("Measured");
    $[51] = t;
    $[52] = t22;
  } else {
    t22 = $[52];
  }
  let t23;
  if ($[53] !== measuredColumns.length || $[54] !== t22) {
    t23 = <th colSpan={t21} scope="colgroup" className="ptl-label border-b border-border bg-ptl-brass-soft px-2 py-2 text-left text-foreground">{t22}</th>;
    $[53] = measuredColumns.length;
    $[54] = t22;
    $[55] = t23;
  } else {
    t23 = $[55];
  }
  const t24 = computedColumns.length;
  let t25;
  if ($[56] !== t) {
    t25 = t("Computed");
    $[56] = t;
    $[57] = t25;
  } else {
    t25 = $[57];
  }
  let t26;
  if ($[58] !== computedColumns.length || $[59] !== t25) {
    t26 = <th colSpan={t24} scope="colgroup" className="ptl-label border-b border-border px-2 py-2 text-left">{t25}</th>;
    $[58] = computedColumns.length;
    $[59] = t25;
    $[60] = t26;
  } else {
    t26 = $[60];
  }
  let t27;
  if ($[61] !== t23 || $[62] !== t26) {
    t27 = <tr>{t20}{t23}{t26}</tr>;
    $[61] = t23;
    $[62] = t26;
    $[63] = t27;
  } else {
    t27 = $[63];
  }
  let t28;
  if ($[64] !== t) {
    t28 = t("No.");
    $[64] = t;
    $[65] = t28;
  } else {
    t28 = $[65];
  }
  let t29;
  if ($[66] !== t28) {
    t29 = <th scope="col" className="px-2 pt-2.5 pb-2 text-left align-bottom font-mono text-[11px] text-ptl-ink-3">{t28}</th>;
    $[66] = t28;
    $[67] = t29;
  } else {
    t29 = $[67];
  }
  let t30;
  if ($[68] !== t) {
    t30 = t("Note");
    $[68] = t;
    $[69] = t30;
  } else {
    t30 = $[69];
  }
  let t31;
  if ($[70] !== t30) {
    t31 = <th scope="col" className="px-2 pt-2.5 pb-2 text-left align-bottom font-mono text-[11px] text-ptl-ink-3">{t30}</th>;
    $[70] = t30;
    $[71] = t31;
  } else {
    t31 = $[71];
  }
  let t32;
  if ($[72] !== computedColumns || $[73] !== measuredColumns) {
    t32 = [...measuredColumns, ...computedColumns].map(_temp);
    $[72] = computedColumns;
    $[73] = measuredColumns;
    $[74] = t32;
  } else {
    t32 = $[74];
  }
  let t33;
  if ($[75] !== t29 || $[76] !== t31 || $[77] !== t32) {
    t33 = <tr className="border-b border-ptl-rule-strong">{t29}{t31}{t32}</tr>;
    $[75] = t29;
    $[76] = t31;
    $[77] = t32;
    $[78] = t33;
  } else {
    t33 = $[78];
  }
  let t34;
  if ($[79] !== t27 || $[80] !== t33) {
    t34 = <thead className="sticky top-0 z-10 bg-card print:static">{t27}{t33}</thead>;
    $[79] = t27;
    $[80] = t33;
    $[81] = t34;
  } else {
    t34 = $[81];
  }
  let t35;
  if ($[82] !== analyzedKeyboard || $[83] !== currentLanguage || $[84] !== useManualSWRMeasurements || $[85] !== useSupportSpringMeasurements) {
    let t36;
    if ($[87] !== currentLanguage || $[88] !== useManualSWRMeasurements || $[89] !== useSupportSpringMeasurements) {
      t36 = key => <AnalyzeDataSheetTableRow key={key.number} isBlackKey={key.color === KeyColor.Black} item={key} locale={currentLanguage} shouldUseManualSWRMeasurements={useManualSWRMeasurements} shouldUseSupportSpringMeasurements={useSupportSpringMeasurements} />;
      $[87] = currentLanguage;
      $[88] = useManualSWRMeasurements;
      $[89] = useSupportSpringMeasurements;
      $[90] = t36;
    } else {
      t36 = $[90];
    }
    t35 = analyzedKeyboard.mapToArray(t36);
    $[82] = analyzedKeyboard;
    $[83] = currentLanguage;
    $[84] = useManualSWRMeasurements;
    $[85] = useSupportSpringMeasurements;
    $[86] = t35;
  } else {
    t35 = $[86];
  }
  let t36;
  if ($[91] !== t35) {
    t36 = <tbody>{t35}</tbody>;
    $[91] = t35;
    $[92] = t36;
  } else {
    t36 = $[92];
  }
  let t37;
  if ($[93] !== t34 || $[94] !== t36) {
    t37 = <table className="w-full border-collapse text-[13px]">{t34}{t36}</table>;
    $[93] = t34;
    $[94] = t36;
    $[95] = t37;
  } else {
    t37 = $[95];
  }
  let t38;
  if ($[96] !== t19 || $[97] !== t37) {
    t38 = <div className="overflow-hidden rounded-md border border-ptl-rule-strong bg-card print:overflow-visible print:rounded-none print:border-0"><div role="region" aria-label={t19} tabIndex={0} className="max-h-[70vh] overflow-auto focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none print:max-h-none print:overflow-visible">{t37}</div></div>;
    $[96] = t19;
    $[97] = t37;
    $[98] = t38;
  } else {
    t38 = $[98];
  }
  return t38;
};
function _temp(column) {
  return <th key={column.abbreviation} scope="col" className="px-2 pt-2.5 pb-2 text-right align-bottom"><span className="block font-mono text-[12px] font-semibold whitespace-nowrap text-ptl-brass">{column.abbreviation}</span><span className="block text-[11px] font-normal text-ptl-ink-3">{column.name}</span></th>;
}